/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';


export const getCodeBlocks = (feature: BaseNode): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const inputs = feature.getInputs();
    const ID = feature.getID();
    const METHOD = feature.getSettingValue('METHOD');
    const INTERFACE = 'current_interface';
    const URL = `${feature.getSettingValue('URL')}`;
    const HEADERS = feature.getSettingValue('HEADERS');
    const CERTIFICATEFILE = feature.getSettingValue('CERTIFICATEFILE');
    let links = project.getNodeLinks(feature, 'http_request_start');

    if (links.length === 0) {
        return [];
    }

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        blockType: BlockType.CODE,
        id: `declare_http_${ID}_response_cb`,
        template: `
static void http_${ID}_response_cb(struct http_response *rsp, enum http_final_call final_data, void *user_data);
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        id: `define_http_${ID}_response_cb`,
        template: `
static void http_${ID}_response_cb(struct http_response *rsp, enum http_final_call final_data, void *user_data)
{
    string data = (char*)(rsp->body_frag_start);
    /****http_${ID}_response_cb****/
    if (final_data == HTTP_DATA_MORE) {
        LOG_INF("Partial data received (%zd bytes)", rsp->data_len);
        return;
    } else if (final_data == HTTP_DATA_FINAL) {
        LOG_INF("All the data received (%zd bytes)", rsp->data_len);
        
    }
    if (rsp->http_status) {
        // if http_status_code does not start with 2, it is an error
        if (rsp->http_status_code / 100 != 2) {
            LOG_ERR("Response status %d", rsp->http_status_code);
            /****callback_http_request_${ID}_failed****/
        }
        LOG_INF("Response status %d %s", rsp->http_status_code, rsp->http_status);
    }

    /****http_${ID}_request_complete****/
}`,
    });

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        id: 'declare_http_recv_buf_ipv4',
        template: `
static uint8_t http_recv_buf_ipv4[1024];
`,
    });

    let headersCode = '';
    const headers: { [key: string]: string } = {};
    for (let i = 0, len = HEADERS.length; i < len; i += 1) {
        const header = HEADERS[i];
        headersCode += `("${header.getSettingValue('NAME')}: " "${header.getSettingValue('VALUE')}" "\\r\\n"),\n`;
    }
    if (headersCode !== '') {
        headersCode = `
    char *const headers[] = {
        ${headersCode}
        NULL
    };
    req.header_fields = (const char **)headers;`;
    }

    const urlVariableName = `http_request_url_${feature.getID()}`;
    for (let i = 0; i < links.length; i += 1) {
        const link = links[i];
        const source = project.getFeature(links[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
        const sourceParams = source.getPort(links[0].getSettingValue('SOURCE_PORT'))?.params;
        const data = project.getNodeParameter(link, 'data');
        let dataCode = '';
        if (METHOD === 'HTTP_POST' || METHOD === 'HTTP_PUT' || METHOD === 'HTTP_PATCH') {
            dataCode = `
string payload_${ID} = ${data};
req.payload = payload_${ID}.c_str();
req.payload_len = strlen(req.payload);`;
        }
        blocks.push({
            location: link.getSourceId(),
            blockType: BlockType.CODE,
            id: link.getID(),
            template: `
string http_request_url_${ID} = ${project.parseCodeExpression(URL, sourceParams, TibboDataType.STRING)};
struct http_parser_url parser = {0};
int ret = http_parser_parse_url(http_request_url_${ID}.c_str(), http_request_url_${ID}.length(), 0, &parser);
if (ret != 0) {
    LOG_ERR("Failed to parse URL");
}
int http_${ID}_sock = setup_http_socket(http_request_url_${ID}.c_str(), &parser);

if (http_${ID}_sock >= 0 && ret == 0) {
    char hostname[256] = {0};
    const char* path = "/";
    const char* url = http_request_url_${ID}.c_str();
    if (parser.field_set & (1 << UF_PATH)) {
        path = url + parser.field_data[UF_PATH].off;
    }
    struct http_request req;
    memset(&req, 0, sizeof(req));
    int32_t timeout = 10 * MSEC_PER_SEC;
    req.method = ${METHOD};
    req.protocol = "HTTP/1.1";
    strncpy(hostname, url + parser.field_data[UF_HOST].off, parser.field_data[UF_HOST].len);
    req.host = hostname;
    req.url = path;
    ${dataCode}
    req.response = http_${ID}_response_cb;
    req.recv_buf = http_recv_buf_ipv4;
    req.recv_buf_len = sizeof(http_recv_buf_ipv4);
    ${headersCode}
    int ret = http_client_req(http_${ID}_sock, &req, timeout, NULL);

    close(http_${ID}_sock);
} else {
    LOG_ERR("Failed to setup HTTP socket");
}
`,
        });
    }

    links = project.getNodeLinks(feature, 'http_data_arrival');
    if (links.length > 0) {
        const link = links[0];
        blocks.push({
            location: `http_${ID}_response_cb`,
            blockType: BlockType.CODE,
            id: link.getSourceId(),
            template: `/****${link.getSourceId()}****/`,
        });
    }

    links = project.getNodeLinks(feature, 'http_complete');
    if (links.length > 0) {
        const link = links[0];
        blocks.push({
            location: `http_${ID}_request_complete`,
            blockType: BlockType.CODE,
            id: link.getSourceId(),
            template: `/****${link.getSourceId()}****/`,
        });
    }

    links = project.getNodeLinks(feature, 'http_error');
    if (links.length > 0) {
        const link = links[0];
        blocks.push({
            location: `callback_http_request_${ID}_failed`,
            blockType: BlockType.CODE,
            id: link.getSourceId(),
            template: `/****${link.getSourceId()}****/`,
        });
    }

    return blocks;
};
